import { useSupportSheet } from "../components/SupportSheet";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Aliased: the default export would shadow the global `Map` constructor.
import MapGL, { Marker } from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  SupportIcon,
} from 'lola-framework-ui-test';
import { BodyCopy } from '../components/BodyCopy';
import { useStore } from '../store/globalState';
import { useGalleryStore } from '../gallery/galleryStore';
import {
  APP_ROUTES,
  CASH_DEPOSIT_ROUTES,
  DEFERRED_ORIGINATION_ROUTES,
  FUND_REQUEST_ROUTES,
  LOLA_ROUTES,
  SEND_ROUTES,
} from '../constants';
import { mapboxStyleUrl, mapboxToken } from '../config';
import {
  CASH_DEPOSIT_MOCK_IP_LOCATION,
  findMockStores,
  findNearestMockStore,
  type CashDepositStore,
  type GeoPoint,
} from '../gallery/cashDepositStoresMock';
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from '../components/Navbar';
import { CloseLight } from '../assets/navIcons';

const SEARCH_RADIUS_MILES = 40;
const GEOLOCATION_TIMEOUT_MS = 8000;
const MAP_EDGE_PADDING = 48;
/** Screen-space grid (px) used to collapse markers that would overlap. */
const CLUSTER_CELL_PX = 57;
const CLUSTER_ZOOM_STEP = 2;
const MAX_ZOOM = 17;

type OriginSource = 'device' | 'ip';

interface Origin extends GeoPoint {
  source: OriginSource;
}

/**
 * Why the map is not centred on the device: the browser said no, the device
 * has no geolocation, or the request timed out. All three land on the IP
 * fallback the backend supplies (mocked here).
 */
type LocationStatus =
  | 'idle'
  | 'requesting'
  | 'device'
  | 'denied'
  | 'unavailable'
  | 'timeout'
  /** The user left their own area to browse a covered metro. */
  | 'browsing';

interface MarkerGroup {
  key: string;
  latitude: number;
  longitude: number;
  stores: CashDepositStore[];
}

const IP_ORIGIN: Origin = {
  latitude: CASH_DEPOSIT_MOCK_IP_LOCATION.latitude,
  longitude: CASH_DEPOSIT_MOCK_IP_LOCATION.longitude,
  source: 'ip',
};

const StoreLogo = ({ store }: { store: CashDepositStore }) => {
  const [failed, setFailed] = useState(false);

  if (!store.logoUrl || failed) {
    return (
      <span className="stores-map-logo-fallback" aria-hidden>
        {store.name.slice(0, 1)}
      </span>
    );
  }

  return (
    <img
      src={store.logoUrl}
      alt=""
      className="stores-map-logo"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Hand the destination to whichever map app the device prefers. Both URLs are
 * https on purpose: the OS routes them to the installed app (Apple Maps,
 * Google Maps, or whatever holds the association on Android) and a desktop
 * browser still opens the web map instead of hitting an unhandled scheme —
 * which is what a `geo:` / `maps:` link would do inside a partner WebView.
 */
const buildDirectionsUrl = (store: CashDepositStore): string => {
  const destination = `${store.address.latitude},${store.address.longitude}`;
  const ua = navigator.userAgent;
  const isApple =
    /iPhone|iPad|iPod/.test(ua) ||
    // iPadOS reports itself as a Mac; the touch count is what gives it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);

  return isApple
    ? `https://maps.apple.com/?daddr=${destination}&q=${encodeURIComponent(store.name)}`
    : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
};

const openDirections = (store: CashDepositStore) => {
  window.open(buildDirectionsUrl(store), '_blank', 'noopener,noreferrer');
};

/**
 * Gallery-safe store locator for the cash-deposit flow.
 *
 * Mapbox GL map over the InComm VRS dump copied into `gallery/fixtures`. The
 * screen asks the device for its position once; if that is refused it falls
 * back to the IP-derived point the backend would send. Tapping a marker only
 * previews a store — the CTA in the card opens directions.
 */
export const StoresMapPage = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useStore();
  const inactiveColor = useGalleryStore(
    (s) => (s.rawTheme.colors as Record<string, string>).inactived,
  );
  /** `backgroundBtn` is a gradient, which browsers reject for borders, rings
   *  and text — those need the solid brand color. */
  const accentSolid = useGalleryStore(
    (s) => (s.rawTheme.colors as Record<string, string>).primaryGradient,
  );
  const { openSupport, supportSheet } = useSupportSheet("cash-deposit");
  const [searchParams] = useSearchParams();
  /** How-to screens open the locator; back must return there instead of the
   *  cash-deposit store list. */
  const fromParam = searchParams.get('from');
  const backRoute =
    fromParam === 'lola'
      ? LOLA_ROUTES.HOW_TO_CASH_DEPOSIT
      : fromParam === 'cash-deposit'
        ? CASH_DEPOSIT_ROUTES.HOW_TO_PAY_IN_CASH
        : fromParam === 'send'
          ? SEND_ROUTES.HOW_TO_DEPOSIT
          : fromParam === 'fund-request'
            ? FUND_REQUEST_ROUTES.HOW_TO_DEPOSIT
            : fromParam === 'deferred-origination'
              ? DEFERRED_ORIGINATION_ROUTES.HOW_TO_DEPOSIT
              : APP_ROUTES.STORES_PAGE;

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    dropzoneColors,
  } = colorConfig;

  const mapRef = useRef<MapRef | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const cardHeightRef = useRef(0);
  const askedForLocation = useRef(false);
  const fittedOrigin = useRef<string | null>(null);

  const [status, setStatus] = useState<LocationStatus>('idle');
  const [browsingCity, setBrowsingCity] = useState('');
  const [origin, setOrigin] = useState<Origin>(IP_ORIGIN);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  /** Bumped when the camera settles so marker clustering re-projects. */
  const [viewportTick, setViewportTick] = useState(0);
  /** Measured so the camera offset and the Mapbox attribution both clear the
   *  card, whatever the address wraps to. */
  const [cardHeight, setCardHeight] = useState(0);

  const stores = useMemo(
    () => findMockStores({ origin, radiusMiles: SEARCH_RADIUS_MILES }),
    [origin],
  );
  const activeStore = useMemo(
    () => stores.find((store) => store.storeId === activeStoreId) ?? null,
    [stores, activeStoreId],
  );
  /** Only computed when the radius search is empty: the user is outside every
   *  metro the dump covers, so offer a jump instead of a blank map. */
  const nearestOutOfRange = useMemo(
    () => (stores.length === 0 ? findNearestMockStore(origin) : null),
    [stores.length, origin],
  );

  /**
   * Chains cluster hard downtown (a dozen CVS within two blocks), and stacked
   * capsules swallow each other's taps. Collapse anything sharing a screen
   * cell into a counter that zooms in, and always keep the open store on its
   * own marker.
   */
  const markerGroups = useMemo<MarkerGroup[]>(() => {
    const map = mapLoaded ? mapRef.current?.getMap() : null;
    if (!map) {
      return stores.map((store) => ({
        key: store.storeId,
        latitude: store.address.latitude,
        longitude: store.address.longitude,
        stores: [store],
      }));
    }

    const cells = new Map<string, CashDepositStore[]>();
    const singles: MarkerGroup[] = [];
    for (const store of stores) {
      if (store.storeId === activeStoreId) {
        singles.push({
          key: store.storeId,
          latitude: store.address.latitude,
          longitude: store.address.longitude,
          stores: [store],
        });
        continue;
      }
      const point = map.project([
        store.address.longitude,
        store.address.latitude,
      ]);
      const cellKey = `${Math.round(point.x / CLUSTER_CELL_PX)}:${Math.round(point.y / CLUSTER_CELL_PX)}`;
      cells.set(cellKey, [...(cells.get(cellKey) ?? []), store]);
    }

    const grouped = [...cells.entries()].map(([cellKey, cellStores]) => ({
      key: cellStores.length === 1 ? cellStores[0].storeId : `cell:${cellKey}`,
      latitude:
        cellStores.reduce((sum, s) => sum + s.address.latitude, 0) /
        cellStores.length,
      longitude:
        cellStores.reduce((sum, s) => sum + s.address.longitude, 0) /
        cellStores.length,
      stores: cellStores,
    }));

    return [...grouped, ...singles];
  }, [stores, activeStoreId, mapLoaded, viewportTick]);

  const requestDeviceLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      setOrigin(IP_ORIGIN);
      return;
    }
    setStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setStatus('device');
        setActiveStoreId(null);
        setOrigin({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          source: 'device',
        });
      },
      (error) => {
        // Denied, position unavailable or timed out — the IP point keeps the
        // locator usable either way.
        setStatus(
          error.code === error.PERMISSION_DENIED
            ? 'denied'
            : error.code === error.TIMEOUT
              ? 'timeout'
              : 'unavailable',
        );
        setActiveStoreId(null);
        setOrigin(IP_ORIGIN);
      },
      { enableHighAccuracy: false, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 300_000 },
    );
  }, []);

  // Asked once on mount. Re-prompting on every render is what makes these
  // screens unusable in a WebView.
  useEffect(() => {
    if (askedForLocation.current) return;
    askedForLocation.current = true;
    requestDeviceLocation();
  }, [requestDeviceLocation]);

  /** Frame the origin plus its nearby stores. Keyed so panning the map by hand
   *  is not undone on the next render. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const key = `${origin.latitude},${origin.longitude},${stores.length}`;
    if (fittedOrigin.current === key) return;
    fittedOrigin.current = key;

    if (stores.length === 0) {
      map.easeTo({
        center: [origin.longitude, origin.latitude],
        zoom: 9,
        duration: 600,
      });
      return;
    }

    const lats = [origin.latitude, ...stores.map((s) => s.address.latitude)];
    const lngs = [origin.longitude, ...stores.map((s) => s.address.longitude)];
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: MAP_EDGE_PADDING, maxZoom: 14, duration: 700 },
    );
  }, [origin, stores, mapLoaded]);

  /** Either the store card or the empty-radius panel covers the map bottom. */
  const panelOpen = Boolean(activeStore) || nearestOutOfRange != null;

  useLayoutEffect(() => {
    const element = cardRef.current;
    if (!element) {
      cardHeightRef.current = 0;
      setCardHeight(0);
      return;
    }
    // offsetHeight, not contentRect: the card's padding and border also cover
    // the map.
    const apply = () => {
      cardHeightRef.current = element.offsetHeight;
      setCardHeight(element.offsetHeight);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(element);
    return () => observer.disconnect();
  }, [activeStoreId, panelOpen]);

  /** Keep the selected marker above the card instead of behind it. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !activeStore) return;
    map.easeTo({
      center: [activeStore.address.longitude, activeStore.address.latitude],
      padding: {
        top: 0,
        left: 0,
        right: 0,
        bottom: cardHeightRef.current + 32,
      },
      duration: 450,
    });
    return () => {
      map.easeTo({
        padding: { top: 0, left: 0, right: 0, bottom: 0 },
        duration: 250,
      });
    };
  }, [activeStore, mapLoaded]);

  const formatDistance = (miles?: number) =>
    miles == null ? null : t('storesMap.distance', { miles: miles.toFixed(1) });

  const formatAddress = (store: CashDepositStore) =>
    [
      store.address.line1,
      [store.address.city, store.address.state].filter(Boolean).join(', '),
    ]
      .filter(Boolean)
      .join(' · ');

  const themeVars = {
    '--store-accent-btn': backgroundBtn,
    '--store-accent-solid': accentSolid ?? bodyCopy,
    '--store-accent-text': textColorBtn,
    '--store-card-border': inactiveColor ?? bodyCopy,
    '--store-card-surface': background,
    '--store-card-text': bodyCopy,
    '--store-card-muted': footerColor,
  } as React.CSSProperties;

  const statusMessage =
    status === 'requesting'
      ? t('storesMap.status.requesting')
      : status === 'browsing'
        ? t('storesMap.status.browsing', { city: browsingCity })
        : status === 'device'
          ? t('storesMap.status.device')
          : status === 'denied'
          ? t('storesMap.status.denied', {
              city: CASH_DEPOSIT_MOCK_IP_LOCATION.city,
            })
          : status === 'timeout'
            ? t('storesMap.status.timeout', {
                city: CASH_DEPOSIT_MOCK_IP_LOCATION.city,
              })
            : status === 'unavailable'
              ? t('storesMap.status.unavailable', {
                  city: CASH_DEPOSIT_MOCK_IP_LOCATION.city,
                })
              : null;

  return (
    <>
    <AuraLayout
      colorConfig={colorConfig}
      className="stores-map-page need-help-header"
    >
      <Layout.Header style={themeVars}>
        <Navbar
          title={t('transactions.needHelp')}
          align="right"
          color={bodyCopy}
          onBackClick={() => navigate(backRoute)}
          onIconClick={openSupport}
          icon={<SupportIcon size={35} colors={dropzoneColors} />}
        />
      </Layout.Header>

      <Layout.Content className="stores-map-section" style={themeVars}>
        <section className="stores-map-copy">
          <GradientText as="h1" textColor={bodyCopy} className="stores-map-title">
            {t('storesMap.title')}
          </GradientText>
          <BodyCopy textColor={bodyCopy} className="stores-map-subtitle">
            {stores.length > 0
              ? t('storesMap.subtitle', { count: stores.length })
              : t('storesMap.subtitleEmpty')}
          </BodyCopy>
        </section>

        <div
          className={`stores-map-canvas${panelOpen ? ' stores-map-canvas--panel-open' : ''}`}
          style={
            { '--stores-map-panel-h': `${cardHeight}px` } as React.CSSProperties
          }
        >
          {mapboxToken ? (
            <MapGL
              ref={mapRef}
              mapboxAccessToken={mapboxToken}
              mapStyle={mapboxStyleUrl}
              initialViewState={{
                latitude: IP_ORIGIN.latitude,
                longitude: IP_ORIGIN.longitude,
                zoom: 11,
              }}
              onLoad={() => setMapLoaded(true)}
              // Regrouping mid-pan would re-render on every frame; markers
              // keep their own lat/lng while the camera moves.
              onMoveEnd={() => setViewportTick((tick) => tick + 1)}
              reuseMaps
              style={{ width: '100%', height: '100%' }}
            >
              {/* Hidden while browsing another metro: the origin is a city
                  centre there, not where the user is. */}
              {status !== 'browsing' && (
                <Marker
                  latitude={origin.latitude}
                  longitude={origin.longitude}
                  anchor="center"
                >
                  <span
                    className={`stores-map-origin stores-map-origin--${origin.source}`}
                    aria-hidden
                  />
                </Marker>
              )}

              {markerGroups.map((group) => {
                const [store] = group.stores;
                const clustered = group.stores.length > 1;
                const selected = !clustered && store.storeId === activeStoreId;
                return (
                  <Marker
                    key={group.key}
                    latitude={group.latitude}
                    longitude={group.longitude}
                    anchor="bottom"
                    // Mapbox stacks markers by latitude; the open one has to
                    // win regardless of where it sits.
                    style={selected ? { zIndex: 3 } : undefined}
                  >
                    <button
                      type="button"
                      className={`stores-map-marker${selected ? ' stores-map-marker--selected' : ''}`}
                      aria-pressed={selected}
                      aria-label={
                        clustered
                          ? t('storesMap.clusterLabel', {
                              count: group.stores.length,
                            })
                          : `${store.name} — ${formatAddress(store)}`
                      }
                      onClick={(event) => {
                        // Without this the map's own click handler closes the
                        // card the same tap just opened.
                        event.stopPropagation();
                        setListOpen(false);
                        if (clustered) {
                          mapRef.current?.easeTo({
                            center: [group.longitude, group.latitude],
                            zoom: Math.min(
                              (mapRef.current.getZoom() ?? 11) +
                                CLUSTER_ZOOM_STEP,
                              MAX_ZOOM,
                            ),
                            duration: 450,
                          });
                          return;
                        }
                        setActiveStoreId(store.storeId);
                      }}
                    >
                      <span
                        className={`stores-map-marker-inner${clustered ? ' stores-map-marker-inner--cluster' : ''}`}
                      >
                        {clustered ? (
                          group.stores.length
                        ) : (
                          <StoreLogo store={store} />
                        )}
                      </span>
                    </button>
                  </Marker>
                );
              })}
            </MapGL>
          ) : (
            <section className="stores-map-setup" role="status">
              <GradientText
                as="h2"
                textColor={bodyCopy}
                className="stores-map-setup-title"
              >
                {t('storesMap.setup.title')}
              </GradientText>
              <BodyCopy textColor={footerColor} className="stores-map-setup-body">
                {t('storesMap.setup.body')}
              </BodyCopy>
              <code className="stores-map-setup-code">MAPBOX_TOKEN</code>
            </section>
          )}

          {statusMessage && !listOpen && (
            <p className="stores-map-status" role="status">
              {statusMessage}
            </p>
          )}

          {!listOpen && (
            <div className="stores-map-actions">
              <button
                type="button"
                className="stores-map-action"
                onClick={requestDeviceLocation}
                disabled={status === 'requesting'}
              >
                {t('storesMap.locate')}
              </button>
              <button
                type="button"
                className="stores-map-action"
                aria-expanded={false}
                disabled={stores.length === 0}
                onClick={() => {
                  setActiveStoreId(null);
                  setListOpen(true);
                }}
              >
                {t('storesMap.showList')}
              </button>
            </div>
          )}

          {stores.length === 0 && nearestOutOfRange && (
            <section ref={cardRef} className="stores-map-empty" role="status">
              <BodyCopy textColor={bodyCopy} className="stores-map-empty-copy">
                {t('storesMap.empty', { miles: SEARCH_RADIUS_MILES })}
              </BodyCopy>
              <button
                type="button"
                className="stores-map-action stores-map-action--active"
                onClick={() => {
                  const store = nearestOutOfRange;
                  setBrowsingCity(store.address.city);
                  setStatus('browsing');
                  setOrigin({
                    latitude: store.address.latitude,
                    longitude: store.address.longitude,
                    source: origin.source,
                  });
                }}
              >
                {t('storesMap.emptyCta', {
                  city: nearestOutOfRange.address.city,
                })}
              </button>
            </section>
          )}

          {listOpen && stores.length > 0 && (
            <section
              className="stores-map-list"
              aria-label={t('storesMap.listLabel')}
            >
              <ScrollFadeIndicator color={bodyCopy} />
              <header className="stores-map-list-head">
                <span className="stores-map-list-title">
                  {t('storesMap.listLabel')}
                </span>
                <button
                  type="button"
                  className="stores-map-action stores-map-action--active"
                  aria-expanded
                  onClick={() => setListOpen(false)}
                >
                  {t('storesMap.showMap')}
                </button>
              </header>
              <ul className="stores-map-list-items">
                {stores.map((store) => (
                  <li key={store.storeId}>
                    <button
                      type="button"
                      className="stores-map-list-item"
                      onClick={() => {
                        setListOpen(false);
                        setActiveStoreId(store.storeId);
                      }}
                    >
                      <span className="stores-map-list-logo">
                        <StoreLogo store={store} />
                      </span>
                      <span className="stores-map-list-text">
                        <span className="stores-map-list-name">{store.name}</span>
                        <span className="stores-map-list-address">
                          {formatAddress(store)}
                        </span>
                      </span>
                      {formatDistance(store.distanceMiles) && (
                        <span className="stores-map-list-distance">
                          {formatDistance(store.distanceMiles)}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Non-modal on purpose: the map stays pannable and other markers
              stay tappable while the card is open. */}
          {activeStore && (
            <section
              ref={cardRef}
              className="stores-map-card"
              aria-label={activeStore.name}
            >
              <div className="stores-map-card-head">
                <span className="stores-map-card-logo">
                  <StoreLogo store={activeStore} />
                </span>
                <span className="stores-map-card-text">
                  <span className="stores-map-card-name">{activeStore.name}</span>
                  <span className="stores-map-card-address">
                    {formatAddress(activeStore)} {activeStore.address.zip}
                  </span>
                  {formatDistance(activeStore.distanceMiles) && (
                    <span className="stores-map-card-meta">
                      <span className="stores-map-card-distance">
                        {formatDistance(activeStore.distanceMiles)}
                      </span>
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="stores-map-card-close"
                  aria-label={t('storesMap.close')}
                  onClick={() => setActiveStoreId(null)}
                >
                  <CloseLight size={31} colors={[bodyCopy, bodyCopy]} />
                </button>
              </div>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                aria-label={t('storesMap.directionsLabel', {
                  name: activeStore.name,
                })}
                onClick={() => openDirections(activeStore)}
              >
                {t('storesMap.directions')}
              </Button>
            </section>
          )}
        </div>
      </Layout.Content>
    </AuraLayout>
    {supportSheet}
  </>
  );
});
